import { useContext, useEffect, useState } from 'react'
import { AuthContext } from 'contexts/AuthContext'
import { Wrapper } from 'components'
import { AppContext } from 'contexts/AppContext'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Card,
  Button,
  Input,
  Select,
  SelectAction,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
  Label,
  Spinner,
  toast,
} from 'keep-react'
import {
  ArrowDown,
  ArrowsDownUp,
  ArrowUp,
  MagnifyingGlass,
  MicrosoftExcelLogo,
} from 'phosphor-react'
import { ResponseStatus } from 'api'
import { renderBadge } from 'utils/renderBadge'
import { RouteNamesAuth } from 'routes/enum'
import { useNavigate } from 'react-router-dom'
import { PolicyListItem, PolicyListResponse } from 'api/types/policies'
import { policiesService } from 'api/services/policies'
import { formatCurrency } from 'utils/currency'

type SortProp =
  | 'Nº da Apólice'
  | 'Produto'
  | 'Tomador'
  | 'Prémio Anual'
  | 'Data da Renovação'
  | 'Estado'
  | 'Capital Seguro'
  | 'NIF'
  | 'Data de Inicio'

type QueryVars = {
  brokerNo: number
  product?: string | undefined
  status: 'DECLINED' | 'LAPSEDQT' | 'QUOTED' | null
  page: number
  nif?: string
}

export const Quotations = () => {
  const { products } = useContext(AppContext)
  const { currentBroker } = useContext(AuthContext)
  const [tableData, setTableData] = useState<PolicyListItem[] | null>(null)
  const [sortProp, setSortProp] = useState<SortProp | null>(null)
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const navigate = useNavigate()

  const brokerNo = currentBroker?.BrokerNo ? currentBroker?.BrokerNo : 0

  const [queryVars, setQueryVars] = useState<QueryVars>({
    brokerNo: brokerNo,
    product: undefined,
    status: null,
    nif: undefined,
    page: 0,
  })

  const fetchQuotations = async () => {
    if (brokerNo === 0) return false

    try {
      setTableData(null)
      setIsLoading(true)
      const quotationList = (await policiesService.getQuotationList({
        ...queryVars,
      })) as PolicyListResponse

      setTableData(quotationList.Policies)
      setTotalCount(quotationList.TotalCount ?? null)
      setIsLoading(false)
    } catch (error) {
      setTableData(null)
    }
  }

  const handleSort = (prop: SortProp) => {
    setSortProp(prev => {
      if (prev === prop) {
        if (sortOrder === 'ASC') {
          setSortOrder('DESC')
        } else {
          setSortOrder('ASC')
        }
      } else {
        setSortOrder('ASC')
      }
      return prop
    })
  }

  const handleExcelDownload = async () => {
    try {
      const response = (await policiesService.getQuotationList({
        ...queryVars,
        output: 'Excel',
      })) as PolicyListResponse

      const contentType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

      // decode base64 in chunks (handles large files)
      const byteChars = atob(response.Output || '')
      const sliceSize = 1024
      const byteArrays = []
      for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
        const slice = byteChars.slice(offset, offset + sliceSize)
        const byteNumbers = new Array(slice.length)
        for (let i = 0; i < slice.length; i++)
          byteNumbers[i] = slice.charCodeAt(i)
        byteArrays.push(new Uint8Array(byteNumbers))
      }

      const blob = new Blob(byteArrays, { type: contentType })
      const url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = Date.now().toString() // respected by most modern browsers
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading Excel file:', error)
      toast.error(
        'Erro ao descarregar o ficheiro Excel. Por favor, tente novamente.',
      )
    }
  }

  const handleSearch = () => {
    setIsLoading(true)
    fetchQuotations()
  }

  useEffect(() => {
    setQueryVars(prev => ({ ...prev, brokerNo: brokerNo }))
  }, [brokerNo])

  useEffect(() => {
    fetchQuotations()
  }, [queryVars.page, queryVars.brokerNo])

  useEffect(() => {
    if (!tableData || !sortProp) return

    const sorted = [...tableData].sort((a, b) => {
      const aValue = a[sortProp]
      const bValue = b[sortProp]

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'ASC' ? aValue - bValue : bValue - aValue
      }
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'ASC'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }
      return 0
    })

    setTableData(sorted)
  }, [sortProp, sortOrder])

  return (
    <div className="w-full flex-1 py-12">
      <Wrapper>
        <div className="lg:pt-8">
          <h2 className="uppercase text-sm font-medium text-blue-900">
            Consulte as suas cotações
          </h2>
          <h1 className="pb-10 text-xxxl font-medium text-primary">Cotações</h1>
          <div className="py-4 lg:py-8">
            <Card className="w-full max-w-full p-4 md:p-6">
              <div className="grid grid-col-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 w-full items-end">
                <div>
                  <Label className="text-body-4 block pb-2">Produto</Label>
                  <Select
                    name="product"
                    onValueChange={val => {
                      if (val === 'none') {
                        setQueryVars(prev => ({ ...prev, product: undefined }))
                        return
                      }
                      setQueryVars(prev => ({ ...prev, product: val }))
                    }}
                  >
                    <SelectAction className="[&_span]:truncate [&_span]:max-w-[90%]">
                      <SelectValue placeholder="Produto" />
                    </SelectAction>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="none">Todos</SelectItem>
                        {products.map(product => (
                          <SelectItem
                            key={product.product}
                            value={product.product}
                          >
                            {product.description}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-body-4 block pb-2">NIF</Label>
                  <Input
                    placeholder="NIF"
                    value={queryVars.nif ?? ''}
                    type="text"
                    onChange={e => {
                      const val = e.target.value
                      setQueryVars(prev => ({
                        ...prev,
                        nif: val === '' ? undefined : val,
                      }))
                    }}
                  />
                </div>

                <div>
                  <Label className="text-body-4 block pb-2">Estado</Label>
                  <Select
                    name="status"
                    onValueChange={val => {
                      if (val === 'none') {
                        setQueryVars(prev => ({ ...prev, status: null }))
                        return
                      }
                      setQueryVars(prev => ({
                        ...prev,
                        status: val as 'DECLINED' | 'LAPSEDQT' | 'QUOTED',
                      }))
                    }}
                  >
                    <SelectAction>
                      <SelectValue placeholder="Estado" />
                    </SelectAction>
                    <SelectContent>
                      <SelectItem value="none">Todos</SelectItem>
                      <SelectGroup>
                        {[
                          { name: 'Em vigor', id: 'QUOTED' },
                          { name: 'Expirada', id: 'LAPSEDQT' },
                        ].map(e => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-4">
                  <Button
                    color="primary"
                    variant="default"
                    className="h-11 gap-2 text-body-5"
                    onClick={handleSearch}
                  >
                    <MagnifyingGlass size={20} />
                    Pesquisar
                  </Button>
                  <Button
                    color="secondary"
                    variant="outline"
                    className="h-11 gap-2 text-body-5"
                    onClick={handleExcelDownload}
                  >
                    <MicrosoftExcelLogo size={20} />
                    Excel
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {isLoading ? (
            <Card className="p-4 w-full max-w-full">
              <Spinner color="primary" className="mx-auto" />
            </Card>
          ) : !tableData ? (
            <Card className="p-4 w-full max-w-full">
              <div className="text-heading-6 text-metal-400">
                Cotações indisponíveis
              </div>
            </Card>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <div className="flex items-center gap-1 whitespace-nowrap">
                        Nº da Cotação
                        <Button
                          variant="link"
                          className={`${
                            sortProp === 'Nº da Apólice'
                              ? 'text-primary-600'
                              : 'text-metal-400'
                          } px-0`}
                          onClick={() => handleSort('Nº da Apólice')}
                        >
                          {sortProp === 'Nº da Apólice' &&
                            sortOrder === 'ASC' && <ArrowUp size={16} />}
                          {sortProp === 'Nº da Apólice' &&
                            sortOrder === 'DESC' && <ArrowDown size={16} />}
                          {sortProp !== 'Nº da Apólice' && (
                            <ArrowsDownUp size={16} />
                          )}
                        </Button>
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        Tomador
                        <Button
                          variant="link"
                          className={`${
                            sortProp === 'Tomador'
                              ? 'text-primary'
                              : 'text-metal-400'
                          } px-0`}
                          onClick={() => handleSort('Tomador')}
                        >
                          {sortProp === 'Tomador' && sortOrder === 'ASC' && (
                            <ArrowUp size={16} />
                          )}
                          {sortProp === 'Tomador' && sortOrder === 'DESC' && (
                            <ArrowDown size={16} />
                          )}
                          {sortProp !== 'Tomador' && <ArrowsDownUp size={16} />}
                        </Button>
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        NIF
                        <Button
                          variant="link"
                          className={`${
                            sortProp === 'NIF'
                              ? 'text-primary'
                              : 'text-metal-400'
                          } px-0`}
                          onClick={() => handleSort('NIF')}
                        >
                          {sortProp === 'NIF' && sortOrder === 'ASC' && (
                            <ArrowUp size={16} />
                          )}
                          {sortProp === 'NIF' && sortOrder === 'DESC' && (
                            <ArrowDown size={16} />
                          )}
                          {sortProp !== 'NIF' && <ArrowsDownUp size={16} />}
                        </Button>
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        Produto
                        <Button
                          variant="link"
                          className={`${
                            sortProp === 'Produto'
                              ? 'text-primary'
                              : 'text-metal-400'
                          } px-0`}
                          onClick={() => handleSort('Produto')}
                        >
                          {sortProp === 'Produto' && sortOrder === 'ASC' && (
                            <ArrowUp size={16} />
                          )}
                          {sortProp === 'Produto' && sortOrder === 'DESC' && (
                            <ArrowDown size={16} />
                          )}
                          {sortProp !== 'Produto' && <ArrowsDownUp size={16} />}
                        </Button>
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1 whitespace-nowrap">
                        Data de Inicio
                        <Button
                          variant="link"
                          className={`${
                            sortProp === 'Data de Inicio'
                              ? 'text-primary'
                              : 'text-metal-400'
                          } px-0`}
                          onClick={() => handleSort('Data de Inicio')}
                        >
                          {sortProp === 'Data de Inicio' &&
                            sortOrder === 'ASC' && <ArrowUp size={16} />}
                          {sortProp === 'Data de Inicio' &&
                            sortOrder === 'DESC' && <ArrowDown size={16} />}
                          {sortProp !== 'Data de Inicio' && (
                            <ArrowsDownUp size={16} />
                          )}
                        </Button>
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1 whitespace-nowrap">
                        Prémio Anual
                        <Button
                          variant="link"
                          className={`${
                            sortProp === 'Prémio Anual'
                              ? 'text-primary'
                              : 'text-metal-400'
                          } px-0`}
                          onClick={() => handleSort('Prémio Anual')}
                        >
                          {sortProp === 'Prémio Anual' &&
                            sortOrder === 'ASC' && <ArrowUp size={16} />}
                          {sortProp === 'Prémio Anual' &&
                            sortOrder === 'DESC' && <ArrowDown size={16} />}
                          {sortProp !== 'Prémio Anual' && (
                            <ArrowsDownUp size={16} />
                          )}
                        </Button>
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        Estado
                        <Button
                          variant="link"
                          className={`${
                            sortProp === 'Estado'
                              ? 'text-primary'
                              : 'text-metal-400'
                          } px-0`}
                          onClick={() => handleSort('Estado')}
                        >
                          {sortProp === 'Estado' && sortOrder === 'ASC' && (
                            <ArrowUp size={16} />
                          )}
                          {sortProp === 'Estado' && sortOrder === 'DESC' && (
                            <ArrowDown size={16} />
                          )}
                          {sortProp !== 'Estado' && <ArrowsDownUp size={16} />}
                        </Button>
                      </div>
                    </TableHead>
                    <TableHead>
                      <div></div>
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {tableData &&
                    tableData.map((policy: any, index: number) => (
                      <TableRow
                        key={`${policy.PolicyNo}-${index}`}
                        className="w-full even:bg-primary-50/25"
                      >
                        <TableCell>{policy['Nº da Apólice']}</TableCell>
                        <TableCell>{policy['Tomador']}</TableCell>
                        <TableCell>{policy['NIF']}</TableCell>
                        <TableCell>{policy['Produto']}</TableCell>
                        <TableCell className="text-right">
                          {policy['Data de Inicio']}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(policy['Prémio Anual'])}
                        </TableCell>
                        <TableCell>
                          {renderBadge(policy['Estado'] as ResponseStatus)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            color="secondary"
                            onClick={() =>
                              navigate(
                                RouteNamesAuth.QUOTATION_DETAILS.replace(
                                  ':policyNo',
                                  policy['Nº da Apólice'],
                                ).replace(':eventNo', policy['LastEvent']),
                              )
                            }
                          >
                            Ver
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <div className="pt-10 text-center">
                <nav className="inline-flex justify-center gap-4  bg-white rounded-full">
                  <Button
                    variant="softBg"
                    color="secondary"
                    disabled={queryVars.page === 0}
                    className="rounded-r-none"
                    onClick={() =>
                      setQueryVars(prev => ({
                        ...prev,
                        page: prev.page > 0 ? prev.page - 1 : 0,
                      }))
                    }
                  >
                    Anterior
                  </Button>
                  <div className="text-body-4 text-metal-500 flex items-center">
                    Página {queryVars.page + 1}
                  </div>
                  <Button
                    variant="softBg"
                    color="secondary"
                    disabled={
                      totalCount !== null
                        ? (queryVars.page + 1) * 50 >= totalCount
                        : false
                    }
                    className="rounded-l-none"
                    onClick={() =>
                      setQueryVars(prev => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                  >
                    Seguinte
                  </Button>
                </nav>
              </div>
            </>
          )}
        </div>
      </Wrapper>
    </div>
  )
}
